import type { TakeoffSheet } from '../../../types/takeoff';
import { resolveSheetFields } from './sheetFields';

export const GALLERY_SORTS = [['drawing', 'Drawing number'], ['title', 'Title']] as const;
export const GALLERY_VIEWS = [['grid', 'Grid'], ['list', 'List']] as const;
export const GALLERY_PREVIEW_SIZES = [['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']] as const;
export const GALLERY_FILTERS = [['all', 'All drawings'], ['takeoff', 'With takeoff'], ['review', 'Needs review']] as const;
export type GallerySort = typeof GALLERY_SORTS[number][0];
export type GalleryView = typeof GALLERY_VIEWS[number][0];
export type GalleryPreviewSize = typeof GALLERY_PREVIEW_SIZES[number][0];
export type GalleryStatusFilter = typeof GALLERY_FILTERS[number][0];

const previewWidths: Record<GalleryPreviewSize, number> = { small: 240, medium: 300, large: 360 };

/** Real previews and incoming placeholders share the same column geometry. */
export function galleryGridColumns(size: GalleryPreviewSize): string {
  return `repeat(auto-fill, minmax(min(100%, ${previewWidths[size]}px), 1fr))`;
}

const compare = new Intl.Collator('en-GB', { numeric: true, sensitivity: 'base' }).compare;

/** Sort the same resolved identifiers/titles the user sees. Parse each sheet
 * once, rather than repeatedly inside the sort comparator. */
export function sortDrawings<T extends Pick<TakeoffSheet, 'id' | 'name' | 'drawing_number' | 'document_title'>>(sheets: T[], sort: GallerySort): T[] {
  return sheets.map(sheet => ({ sheet, ...resolveSheetFields(sheet) }))
    .sort((a, b) => {
      if (sort === 'title') {
        return compare(a.drawingName, b.drawingName)
          || compare(a.drawingNo ?? '', b.drawingNo ?? '')
          || compare(a.sheet.id, b.sheet.id);
      }
      if (!a.drawingNo || !b.drawingNo) {
        if (a.drawingNo) return -1;
        if (b.drawingNo) return 1;
      }
      return compare(a.drawingNo ?? '', b.drawingNo ?? '')
        || compare(a.drawingName, b.drawingName)
        || compare(a.sheet.id, b.sheet.id);
    })
    .map(({ sheet }) => sheet);
}
